import { useMemo } from 'react';
import useComplianceOverviewCountsQuery from '../../hooks/data-fetching/useComplianceOverviewCountsQuery';
import {
  RECERT_RANGE_FILTER_VALUES,
  RECERT_RANGE_TO_FILTER_MAP,
} from './constantsV2';
import messages from './messages';

const RECERT_RANGE_FILTER_OPTION_ORDER = [
  RECERT_RANGE_FILTER_VALUES.LATE,
  RECERT_RANGE_FILTER_VALUES.UNDER_30,
  RECERT_RANGE_FILTER_VALUES.BETWEEN_31_AND_60,
  RECERT_RANGE_FILTER_VALUES.BETWEEN_61_AND_90,
  RECERT_RANGE_FILTER_VALUES.BETWEEN_90_AND_120,
  RECERT_RANGE_FILTER_VALUES.UPCOMING,
];

const RECERT_RANGE_FILTER_LABEL_MESSAGES = {
  [RECERT_RANGE_FILTER_VALUES.LATE]: messages.planningRangePastDue,
  [RECERT_RANGE_FILTER_VALUES.UNDER_30]: messages.planningRangeUnder30,
  [RECERT_RANGE_FILTER_VALUES.BETWEEN_31_AND_60]:
    messages.planningRangeBetween31And60,
  [RECERT_RANGE_FILTER_VALUES.BETWEEN_61_AND_90]:
    messages.planningRangeBetween61And90,
  [RECERT_RANGE_FILTER_VALUES.BETWEEN_90_AND_120]:
    messages.planningRangeBetween90And120,
  [RECERT_RANGE_FILTER_VALUES.UPCOMING]: messages.planningRangeUpcoming,
};

// Inverse of RECERT_RANGE_TO_FILTER_MAP: filter-value id -> backend bucket key.
const FILTER_VALUE_TO_BUCKET_KEY = Object.fromEntries(
  Object.entries(RECERT_RANGE_TO_FILTER_MAP).map(([bucketKey, filterValue]) => [
    filterValue,
    bucketKey,
  ]),
);

/**
 * Builds the `options` array consumed by the shared `PlanningRangeFilter`
 * component on the portfolio Compliance Summary "All Open Certs" tab.
 * ---
 * Counts come from the /compliance-overview/counts endpoint scoped by the
 * same filter/search the rows request sends, minus recertRange: a tile's
 * count must not depend on which tiles are selected.
 */
const usePlanningRangeFilter = ({
  intl,
  organizationId,
  includeNotStartedCerts,
  filter,
  searchText,
  enabled,
}) => {
  const filterExcludingRecertRange = useMemo(() => {
    const { recertRange, ...rest } = filter ?? {};
    return rest;
  }, [filter]);

  const { buckets } = useComplianceOverviewCountsQuery({
    organizationId,
    includeNotStartedCerts,
    filter: filterExcludingRecertRange,
    searchText,
    options: { enabled },
  });

  const planningRangeOptions = useMemo(() => {
    return RECERT_RANGE_FILTER_OPTION_ORDER.map((filterValue) => {
      const bucketKey = FILTER_VALUE_TO_BUCKET_KEY[filterValue];
      return {
        id: filterValue,
        label: intl.formatMessage(
          RECERT_RANGE_FILTER_LABEL_MESSAGES[filterValue],
        ),
        count: buckets?.[bucketKey] ?? 0,
      };
    });
  }, [buckets, intl]);

  return { planningRangeOptions };
};

export default usePlanningRangeFilter;
